import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, Send, Loader2, MapPin, UserPlus, MessageSquare, X, Search,
  CheckCircle2, ShoppingCart, Factory, Globe, Users, TrendingUp, Star, Cog, Briefcase,
  ArrowRight, Eye,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STEPS = [
  { n: 1, title: "FIND MEMBER", desc: "Search and discover relevant B2B members based on industry and location." },
  { n: 2, title: "VIEW PROFILE", desc: "View limited profile information of the member." },
  { n: 3, title: "SEND REQUEST", desc: "Send a short note request to connect with the member." },
  { n: 4, title: "ACCEPTED", desc: "Member reviews and accepts your request." },
  { n: 5, title: "START CHATTING", desc: "Once accepted, start chatting and build your business relationship." },
];

const CATEGORIES = [
  { key: "buyers-importers", label: "Buyers / Importers", icon: ShoppingCart },
  { key: "suppliers-manufacturers", label: "Suppliers / Manufacturers", icon: Factory },
  { key: "exporters", label: "Exporters", icon: Globe },
  { key: "distributors", label: "Distributors", icon: Users },
  { key: "investors", label: "Investors", icon: TrendingUp },
  { key: "advisors", label: "Advisors", icon: Star },
  { key: "service-providers", label: "Service Providers", icon: Cog },
  { key: "business-professionals", label: "Business Professionals", icon: Briefcase },
];

const FEATURES = [
  { title: "Verified Members", desc: "All members are verified for a safer network.", icon: ShieldCheck },
  { title: "Global Reach", desc: "Connect with businesses from multiple countries.", icon: Globe },
  { title: "Secure Platform", desc: "Your data and communications are always secure.", icon: Lock },
  { title: "Real Opportunities", desc: "Build valuable partnerships and grow your business.", icon: TrendingUp },
];

function RequestModal({ member, onClose, onSent }) {
  const [note, setNote] = useState("");
  const [working, setWorking] = useState(false);
  const send = async () => {
    setWorking(true);
    try {
      await api.post("/connections/request", { to_user_id: member.id, note });
      toast.success(`Request sent to ${member.name}`);
      onSent(member.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setWorking(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="request-modal">
      <div className="absolute inset-0 bg-navy-950/80" onClick={onClose} />
      <div className="relative card-surface hairline-gold w-full max-w-lg p-7">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-300" data-testid="request-modal-close"><X size={16} /></button>
        <p className="gold-kicker mb-2">Send Request</p>
        <h3 className="font-display text-lg font-bold text-slate-100" data-testid="request-modal-member">{member.name} · {member.company}</h3>
        <label className="block mt-5 text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Short Note — introduce yourself, your intent, and how you see value in connecting</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={500}
          placeholder="Hello, I'm interested in exploring business opportunities in the renewable energy sector. Let's connect and see how we can create value together."
          data-testid="request-note-input" className="input-dark text-sm w-full" />
        <p className="text-[10px] font-mono text-slate-600 mt-1 text-right">{note.length}/500</p>
        <button onClick={send} disabled={working || note.trim().length < 10} data-testid="send-request-btn" className="btn-gold w-full mt-3 !py-3 text-xs">
          {working ? <Loader2 size={14} className="animate-spin" /> : <><Send size={13} /> SEND REQUEST</>}
        </button>
      </div>
    </div>
  );
}

function ProfileModal({ member, onClose, onConnect }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="member-profile-modal">
      <div className="absolute inset-0 bg-navy-950/80" onClick={onClose} />
      <div className="relative card-surface hairline-gold w-full max-w-md p-7">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-slate-300" data-testid="profile-modal-close"><X size={16} /></button>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-sm bg-navy-700 border border-slate-700 flex items-center justify-center font-display font-bold text-gold text-2xl shrink-0">{member.name.charAt(0)}</div>
          <div>
            <h3 className="font-display text-xl font-bold text-slate-100" data-testid="profile-modal-name">{member.name}</h3>
            <p className="text-sm text-gold">{member.company}</p>
          </div>
        </div>
        <div className="mt-6 space-y-3 text-xs font-mono" data-testid="profile-modal-fields">
          {[["INDUSTRY", member.industry || "—"], ["LOCATION", [member.location, member.country].filter(Boolean).join(", ") || "—"],
            ["VERIFICATION", member.verification_level], ["MEMBER SINCE", member.member_since]].map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-slate-800 pb-2">
              <span className="text-slate-500 uppercase tracking-widest">{k}</span>
              <span className="text-slate-200 text-right">{v}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[10px] font-mono text-slate-600 flex items-center gap-1.5"><Lock size={11} className="text-gold" /> Limited profile — full business contact intelligence unlocks after connection.</p>
        <button onClick={() => onConnect(member)} data-testid="profile-connect-btn" className="btn-gold w-full mt-5 !py-3 text-xs">
          <UserPlus size={13} /> SEND CONNECTION REQUEST
        </button>
      </div>
    </div>
  );
}

export default function Members() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState(null);
  const [denied, setDenied] = useState(false);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [target, setTarget] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback((cat = category) => {
    const params = cat ? { category: cat } : {};
    api.get("/members/directory", { params }).then((r) => setMembers(r.data)).catch((err) => {
      if (err.response?.status === 401) setDenied(true);
      else toast.error("Could not load members");
    });
  }, [category]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const pickCategory = (key) => {
    const next = category === key ? "" : key;
    setCategory(next);
    load(next);
  };

  const filtered = (members || []).filter((m) => {
    const blob = `${m.name} ${m.company} ${m.industry} ${m.location} ${m.country}`.toLowerCase();
    return blob.includes(q.toLowerCase());
  });

  const actionFor = (m) => {
    if (m.connection_status === "ACCEPTED") return <Link to="/connections" data-testid={`message-${m.id}`} className="btn-gold !px-4 !py-2 text-[11px] flex-1"><MessageSquare size={12} /> MESSAGE</Link>;
    if (m.connection_status === "PENDING_SENT") return <button disabled data-testid={`pending-${m.id}`} className="btn-outline !px-4 !py-2 text-[11px] flex-1 opacity-60 cursor-default"><CheckCircle2 size={12} /> REQUEST SENT</button>;
    if (m.connection_status === "PENDING_RECEIVED") return <Link to="/connections" data-testid={`respond-${m.id}`} className="btn-gold !px-4 !py-2 text-[11px] flex-1">RESPOND TO REQUEST</Link>;
    if (m.connection_status === "DECLINED") return <button disabled data-testid={`declined-${m.id}`} className="btn-outline !px-4 !py-2 text-[11px] flex-1 opacity-50 cursor-default">DECLINED</button>;
    return <button onClick={() => setTarget(m)} data-testid={`connect-${m.id}`} className="btn-gold !px-4 !py-2 text-[11px] flex-1"><UserPlus size={12} /> SEND REQUEST</button>;
  };

  return (
    <div className="bg-navy-950" data-testid="members-page">
      {/* POSTER HERO */}
      <section className="border-b border-slate-800" data-testid="members-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-14 text-center">
          <p className="gold-kicker mb-3" data-testid="members-kicker">10XEXIM — A Global B2B Membership & Business Network</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-100" data-testid="members-headline">
            CONNECTING<br />GLOBAL <span className="text-gold">B2B MEMBERS</span>
          </h1>
          <p className="mt-3 text-base md:text-lg font-semibold text-slate-300 tracking-wide">Find. Connect. Collaborate. Grow.</p>
          <p className="mt-3 text-sm text-slate-400 max-w-xl mx-auto leading-relaxed">
            Discover verified businesses, buyers, suppliers, manufacturers, exporters, importers and business professionals from around the world.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="hero-register-btn" className="btn-gold text-xs">REGISTER NOW</Link>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — poster 5 steps */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14" data-testid="members-steps">
        <h2 className="font-display text-xl md:text-2xl font-bold text-slate-100 text-center tracking-wide">HOW IT WORKS</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
          {STEPS.map((s) => (
            <div key={s.n} className="card-surface p-5 text-center hover:border-gold/40 transition-colors" data-testid={`member-step-${s.n}`}>
              <div className="w-11 h-11 mx-auto rounded-full border border-gold/50 bg-navy-900 flex items-center justify-center font-mono font-bold text-gold text-sm">{s.n}</div>
              <p className="text-[11px] font-bold text-slate-200 mt-3 tracking-wide">{s.title}</p>
              <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MEMBER CATEGORIES — functional filters */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14" data-testid="member-categories">
        <h2 className="font-display text-xl md:text-2xl font-bold text-slate-100 text-center tracking-wide">MEMBER CATEGORIES</h2>
        <p className="text-center text-xs text-slate-500 mt-2">Select a category to filter the live directory.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => pickCategory(c.key)} data-testid={`category-${c.key}`}
              className={`card-surface p-6 text-center transition-all hover:-translate-y-1 hover:border-gold/40 ${category === c.key ? "border-gold/60 bg-gold/5" : ""}`}>
              <c.icon size={22} className={`mx-auto transition-colors ${category === c.key ? "text-gold" : "text-slate-400"}`} />
              <p className="text-xs font-semibold text-slate-200 mt-3">{c.label}</p>
            </button>
          ))}
        </div>
      </section>

      {/* DIRECTORY */}
      <section id="member-directory" className="max-w-7xl mx-auto px-4 md:px-8 mt-14">
        <div className="flex items-center gap-3 card-surface px-4 py-3" data-testid="members-search">
          <Search size={15} className="text-slate-500 shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name, company, industry or location"
            data-testid="members-search-input" className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none" />
        </div>

        {denied && (
          <div className="card-surface p-10 text-center mt-8" data-testid="members-login-prompt">
            <Lock size={22} className="text-gold mx-auto mb-3" />
            <p className="text-slate-300 text-sm">The exclusive member directory is visible to registered 10XGLOBAL members.</p>
            <div className="mt-5 flex justify-center gap-3">
              <Link to="/login" data-testid="members-login-btn" className="btn-gold text-xs">SIGN IN</Link>
              <Link to="/membership" data-testid="members-join-btn" className="btn-outline text-xs">BECOME A MEMBER</Link>
            </div>
          </div>
        )}

        {members && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="members-grid">
            {filtered.map((m) => (
              <div key={m.id} className="card-surface p-5 hover:border-gold/40 transition-colors" data-testid={`member-card-${m.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-sm bg-navy-700 border border-slate-700 flex items-center justify-center font-display font-bold text-gold text-lg shrink-0">{m.name.charAt(0)}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-100 truncate">{m.name}</p>
                    <p className="text-xs text-gold truncate">{m.company}</p>
                    <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><MapPin size={10} />{[m.industry, m.location || m.country].filter(Boolean).join(" · ") || "Global"}</p>
                  </div>
                  <ShieldCheck size={15} className="text-emerald-400 shrink-0" />
                </div>
                <div className="mt-4 flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-slate-600">
                  <span>{m.verification_level}</span>
                  <span>Member since {m.member_since}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setViewing(m)} data-testid={`view-profile-${m.id}`} className="btn-outline !px-3 !py-2 text-[11px]"><Eye size={12} /></button>
                  {actionFor(m)}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="card-surface p-10 text-center text-sm text-slate-500 sm:col-span-2 lg:col-span-3" data-testid="members-empty">
                {category ? "No members in this category yet — the network grows with every verified membership." : "No members match your search."}
              </div>
            )}
          </div>
        )}
      </section>

      {/* KEY FEATURES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mt-14" data-testid="key-features">
        <div className="card-surface hairline-gold p-6 md:p-8">
          <h2 className="font-display text-lg font-bold text-gold text-center tracking-widest">KEY FEATURES</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-3.5" data-testid={`feature-${f.title.toLowerCase().replace(/ /g, "-")}`}>
                <f.icon size={20} className="text-gold shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-slate-100">{f.title}</p>
                  <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 my-14" data-testid="members-final-cta">
        <div className="card-surface p-8 md:p-10 flex flex-wrap items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-100">Join 10XEXIM Today!</h2>
            <p className="mt-1.5 text-sm text-slate-400">One Global Network. Endless Business Opportunities.</p>
          </div>
          <Link to="/register" data-testid="final-register-btn" className="btn-gold text-xs">REGISTER NOW <ArrowRight size={13} /></Link>
        </div>
      </section>

      {viewing && <ProfileModal member={viewing} onClose={() => setViewing(null)} onConnect={(m) => { setViewing(null); setTarget(m); }} />}
      {target && <RequestModal member={target} onClose={() => setTarget(null)} onSent={() => { setTarget(null); load(); }} />}
    </div>
  );
}
